import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { PenLine, Eraser, Keyboard } from "lucide-react";

/**
 * Drawn signature pad.
 *
 * Props:
 *   value    string — current signature. Either a data URL (image/png) or a typed name.
 *   onChange fn(string) — emits a data URL while drawing, or a plain string while typing.
 *   label    string — short label shown above the pad ("Parent signature", etc.).
 *   testid   string — base for data-testid attributes.
 *
 * UX: two tabs, "Draw" (default) and "Type". Drawing stores a trimmed PNG data URL
 * using pointer events (works for mouse + touch + Apple Pencil). Typing stays as
 * plain text for legacy compatibility — the filled-PDF code already handles both.
 */
export default function SignaturePad({ value, onChange, label = "Signature", testid = "signature-pad", required = false }) {
  const isDataUrl = typeof value === "string" && value.startsWith("data:image");
  const [mode, setMode] = useState(isDataUrl || !value ? "draw" : "type");
  const [typed, setTyped] = useState(isDataUrl ? "" : (value || ""));
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const hasStrokes = useRef(false);

  // Resize the canvas bitmap to match its rendered size (HiDPI safe)
  useEffect(() => {
    if (mode !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.2;
      ctx.strokeStyle = "#0f2f2a";
      // If we have an existing data URL, paint it back after a resize.
      if (isDataUrl) {
        const img = new Image();
        img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
        img.src = value;
      }
    };
    resize();
    const obs = new ResizeObserver(resize);
    obs.observe(canvas);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
    try { e.target.setPointerCapture?.(e.pointerId); } catch {}
  };

  const move = (e) => {
    if (!drawing.current) return;
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    hasStrokes.current = true;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    if (hasStrokes.current) {
      try {
        const url = canvasRef.current.toDataURL("image/png");
        onChange(url);
      } catch {
        // canvas tainting or encode failure — ignore, user will have to retry
      }
    }
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    hasStrokes.current = false;
    onChange("");
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">
          {label}{required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="flex rounded-lg border border-stone-200 bg-white overflow-hidden text-[11px] font-semibold">
          <button
            type="button"
            data-testid={`${testid}-tab-draw`}
            onClick={() => setMode("draw")}
            className={`px-3 py-1 flex items-center gap-1 ${mode === "draw" ? "bg-emerald-700 text-white" : "text-stone-600 hover:bg-stone-50"}`}
          >
            <PenLine className="w-3 h-3" /> Draw
          </button>
          <button
            type="button"
            data-testid={`${testid}-tab-type`}
            onClick={() => setMode("type")}
            className={`px-3 py-1 flex items-center gap-1 border-l border-stone-200 ${mode === "type" ? "bg-emerald-700 text-white" : "text-stone-600 hover:bg-stone-50"}`}
          >
            <Keyboard className="w-3 h-3" /> Type
          </button>
        </div>
      </div>

      {mode === "draw" ? (
        <div className="relative">
          <canvas
            ref={canvasRef}
            data-testid={`${testid}-canvas`}
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerLeave={end}
            className="block w-full h-36 sm:h-40 rounded-xl border border-stone-300 bg-white touch-none cursor-crosshair"
            style={{ backgroundImage: "linear-gradient(to bottom, transparent 0 calc(100% - 1px), #e7e5e4 calc(100% - 1px))" }}
          />
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[11px] text-stone-500">Sign above with finger, mouse, or stylus.</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid={`${testid}-clear`}
              onClick={clear}
              className="h-7 px-2 text-stone-600 text-xs"
            >
              <Eraser className="w-3 h-3 mr-1" /> Clear
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <input
            type="text"
            data-testid={`${testid}-typed`}
            value={typed}
            onChange={(e) => { setTyped(e.target.value); onChange(e.target.value); }}
            placeholder="Type your full legal name"
            className="w-full rounded-xl border border-stone-300 bg-white px-3 h-11 text-sm"
            autoComplete="off"
          />
          <p className="text-[11px] text-stone-500 mt-1.5">Typed full legal name is accepted as an electronic signature.</p>
        </div>
      )}
    </div>
  );
}
